import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { SmartCatalogTheme as ThemePayload } from "@/services/walter-assistant";
import { BadgeQuestionMark, Sparkles, Star } from "lucide-react";
import { memo, useState } from "react";
import { FormationThumbnail } from "./formation-thumbnail";
import { FundingBadge } from "./funding-badge";
import { ModalityBadge } from "./modality-badge";
import { ProductRating } from "./product-rating";
import { DISPLAY_INITIAL_N_PER_THEME, DISPLAY_TOP_N_PER_THEME, formatDurationHours, formatPersonalFunding, formatPriceEUR } from "./smart-catalog.utils";

type Props = {
    theme: ThemePayload;
};

/**
 * Carte d'un thème — Section 3 de la spec.
 *
 * Affiche une matrice de produits (colonnes : durée, modalité,
 * financement, reste à charge) triés par score décroissant. Les produits
 * au-delà du top 4 (`DISPLAY_TOP_N_PER_THEME`) sont grisés (présents mais
 * non mis en avant). Seuls les `DISPLAY_INITIAL_N_PER_THEME` premiers sont
 * affichés par défaut ; « Voir plus » déplie le reste si nécessaire. Le
 * meilleur produit du thème (`is_top_pick`, soit le
 * premier) porte une étoile. Le verbatim du meilleur produit est affiché
 * sous la matrice pour aider le pitch.
 *
 * L'ordre des thèmes et des produits est imposé par le backend (scorer
 * déterministe côté walter-crm) — aucun tri côté front.
 */
function SmartCatalogThemeImpl({ theme }: Props) {
    const [expanded, setExpanded] = useState(false);
    const products = theme.products ?? [];
    if (products.length === 0) return null;

    const hasHiddenProducts = products.length > DISPLAY_INITIAL_N_PER_THEME;
    const visibleProducts = expanded ? products : products.slice(0, DISPLAY_INITIAL_N_PER_THEME);
    const hiddenCount = products.length - DISPLAY_INITIAL_N_PER_THEME;

    const verbatim = theme.verbatim;
    const verbatimPending = !verbatim || verbatim.verbatim_status === "pending";
    const transition = verbatim?.transition_sentence?.trim() || "";
    const bullet1 = verbatim?.bullet_1?.trim() || "";
    const bullet2 = verbatim?.bullet_2?.trim() || "";
    const hasVerbatim = Boolean(transition || bullet1 || bullet2);

    return (
        <section aria-label={`Thème ${theme.theme_title}`} className="flex flex-col gap-4 rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
            <Accordion type="single" collapsible>
                <AccordionItem value="content" className="border-b-0 ">
                    <AccordionTrigger className="py-0  hover:no-underline">
                        <header className="mr-4 flex w-full items-start gap-3">
                            <FormationThumbnail productCodes={[products[0]?.product_code, products[1]?.product_code]} alt={theme.theme_title} />
                            <div className="flex h-full w-full flex-col items-start justify-start gap-2">
                                {verbatimPending ? (
                                    /* Placeholder calqué sur la structure du verbatim réel (flex-col)
                   pour éviter les sauts de page à la réception des verbatims. */
                                    <div className="flex w-full flex-col items-start justify-start rounded-md bg-indigo-50/50 p-3" aria-label="Aide au pitch en cours de génération…">
                                        <div className="flex w-full min-w-0 items-start gap-1.5">
                                            <Sparkles className="mt-0.5 size-4 shrink-0 text-indigo-300" aria-hidden />
                                            <Skeleton className="h-5 min-w-0 flex-1 rounded bg-indigo-100" />
                                        </div>
                                        <div className="ml-5 mt-1.5 flex w-full flex-col gap-1.5">
                                            <Skeleton className="h-3 w-5/6 rounded bg-indigo-100" />
                                            <Skeleton className="h-3 w-5/6 rounded bg-indigo-100" />
                                        </div>
                                    </div>
                                ) : hasVerbatim ? (
                                    <div className="flex w-full flex-col items-start justify-start rounded-md bg-indigo-50/50 p-3">
                                        {transition ? (
                                            <p className="flex items-start gap-1.5 text-left text-sm font-medium leading-snug text-indigo-900">
                                                <Sparkles className="mt-0.5 size-4 shrink-0 text-indigo-300" aria-hidden />
                                                <span>« {transition} »</span>
                                            </p>
                                        ) : null}
                                        {bullet1 || bullet2 ? (
                                            <ul className={cn("mt-1.5 flex flex-col items-start gap-1 text-sm leading-snug text-indigo-900/90", transition ? "ml-5" : "")}>
                                                {bullet1 ? <li className="flex items-start justify-start gap-1.5 text-left">• {bullet1}</li> : null}
                                                {bullet2 ? <li className="flex items-start justify-start gap-1.5 text-left">• {bullet2}</li> : null}
                                            </ul>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        </header>
                    </AccordionTrigger>
                    <AccordionContent className="mt-4 flex flex-col gap-4">
                        <div className="overflow-hidden rounded-md border border-gray-100">
                            <TooltipProvider>
                                <table className="w-full table-fixed border-collapse text-left text-sm">
                                    {/* col widths: titre (flex), format 14%, durée 10%, financeur 12%, financement 14%, note 10%, sessions 14% */}
                                    <colgroup>
                                        <col />
                                        <col className="w-[14%]" />
                                        <col className="w-[10%]" />
                                        <col className="w-[12%]" />
                                        <col className="w-[14%]" />
                                        <col className="w-[10%]" />
                                        <col className="w-[14%]" />
                                    </colgroup>
                                    <thead>
                                        <tr className="bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                                            <th scope="col" className="truncate text-ellipsis px-2 py-1.5">
                                                Titre
                                            </th>
                                            <th scope="col" className="px-2 py-1.5">
                                                Format
                                            </th>
                                            <th scope="col" className="px-2 py-1.5">
                                                Durée
                                            </th>
                                            <th scope="col" className="px-2 py-1.5">
                                                Financeur
                                            </th>
                                            <th scope="col" className="px-2 py-1.5">
                                                Financement
                                            </th>
                                            <th scope="col" className="px-2 py-1.5">
                                                Notes
                                            </th>
                                            <th scope="col" className="px-2 py-1.5">
                                                Sessions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleProducts.map((product, idx) => {
                                            const greyed = idx >= DISPLAY_TOP_N_PER_THEME;
                                            const pf = product.personal_funding;
                                            return (
                                                <tr
                                                    key={product.product_code}
                                                    className={cn("border-t border-gray-100 align-top", greyed ? "text-gray-400 opacity-60" : "text-gray-700")}
                                                    aria-label={`Produit ${product.name}`}
                                                >
                                                    <td className="max-w-[260px] px-2 py-1.5" title={product.name + " - " + product.product_code}>
                                                        <span className="flex items-start">
                                                            {product.is_top_pick ? <Star className="mr-1 mt-0.5 size-4 shrink-0 fill-amber-400 text-amber-400" aria-hidden /> : null}
                                                            <span>{product.name}</span>
                                                        </span>
                                                    </td>
                                                    <td className="whitespace-nowrap px-2 py-1.5">
                                                        <ModalityBadge modality={product.modality} />
                                                    </td>
                                                    <td className="whitespace-nowrap px-2 py-1.5">{formatDurationHours(product.duration_hours)}</td>
                                                    <td className="whitespace-nowrap px-2 py-1.5">
                                                        <FundingBadge code={(product.eligible_fundings ?? [])[0]?.code ?? null} />
                                                    </td>

                                                    <td className="whitespace-nowrap px-2 py-1.5">
                                                        {product.listing_price ? (
                                                            <span className="inline-flex flex-col items-baseline gap-0.5">
                                                                <span>{formatPriceEUR(product.listing_price)}</span>
                                                                {product.funding_price ? <span className="text-xs text-gray-500">PEC max: {formatPriceEUR(product.funding_price)}</span> : null}
                                                                {pf ? (
                                                                    <span className="text-xs text-gray-500">
                                                                        {formatPersonalFunding(pf)}
                                                                        {!pf.exact ? (
                                                                            <Tooltip>
                                                                                <TooltipTrigger asChild>
                                                                                    <BadgeQuestionMark className="mb-0.5 ml-1 inline-block size-3.5 text-gray-500" />
                                                                                </TooltipTrigger>
                                                                                <TooltipContent className="max-w-xs text-center leading-snug">
                                                                                    Le niveau de prise en charge exact dépend du <br />
                                                                                    profil du prospect et sera connu après
                                                                                    <br />
                                                                                    la demande de prise en charge.
                                                                                </TooltipContent>
                                                                            </Tooltip>
                                                                        ) : null}
                                                                    </span>
                                                                ) : null}
                                                            </span>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-1.5">
                                                        <ProductRating avg={product.ratings_avg} count={product.ratings_count} />
                                                    </td>
                                                    <td className="px-2 py-1.5">
                                                        {(product.next_sessions ?? []).length > 0 ? (
                                                            <span className="inline-flex flex-col gap-0.5 text-xs text-gray-600">
                                                                {product.next_sessions.slice(0, 3).map((s, i) => (
                                                                    <span key={i} className="whitespace-nowrap">
                                                                        {new Date(s.starts_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                                                                        {s.seats_left != null ? <span className="ml-1 text-gray-400">({s.seats_left} pl.)</span> : null}
                                                                    </span>
                                                                ))}
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-gray-400">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </TooltipProvider>
                            {hasHiddenProducts ? (
                                <div className="border-t border-gray-100 px-2 py-1">
                                    <Button
                                        type="button"
                                        variant="link"
                                        size="sm"
                                        className="h-7 w-full text-xs text-blue-600 hover:text-blue-900"
                                        onClick={() => setExpanded(prev => !prev)}
                                        aria-expanded={expanded}
                                    >
                                        {expanded ? "Voir moins" : `Voir plus de formats et financements (${hiddenCount})`}
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </section>
    );
}

export const SmartCatalogTheme = memo(SmartCatalogThemeImpl);
